import React, { useMemo, useState } from 'react';
import styled from '@emotion/styled';
import toast from '../../utils/toast';
import {
  palette,
  radius,
  spacing,
  typography,
  shadow,
  layout,
} from '../../style-utils/theme';
import { down } from '../../style-utils/media-queries';
import { AppButton } from '../button/app-button';
import { UnitLimitError, allocateUnits } from '../../api/units-client';
import { UseUnitAllowanceResult } from '../hooks/use-unit-allowance';
import { BuyUnitsDialog } from './buy-units-dialog';

const Panel = styled.section`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: ${spacing.sm};
  margin-bottom: ${spacing.md};
  padding: ${spacing.sm} ${spacing.md};
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  background: ${palette.surface};
  box-shadow: ${shadow.xs};

  ${down('sm')} {
    align-items: stretch;
    flex-direction: column;
  }
`;

const Copy = styled.div`
  flex: 1 1 240px;
  min-width: 0;
`;

const Heading = styled.h2`
  margin: 0 0 ${spacing.half};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;
  font-weight: ${typography.weight.semibold};
  color: ${palette.ink};
  /* This heading interpolates a BUILDING NAME, which is arbitrary user text.
     Measured at 320px with a 66-character name: 549px wide, pushing the whole
     document to 748px. The global break-word floor does not help — it breaks at
     paint time but leaves min-content at the full token, and this heading sits
     in a content-sized panel. anywhere is the value that collapses min-content.
     NO BACKTICKS IN THIS COMMENT: styled template literal. */
  overflow-wrap: anywhere;
`;

const Detail = styled.p`
  margin: 0;
  font-family: ${typography.ui};
  font-size: ${typography.size.sm}px;
  line-height: ${typography.leading.normal};
  color: ${palette.slate};

  & b {
    color: ${palette.ink};
    font-weight: ${typography.weight.semibold};
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.half};
`;

const Label = styled.label`
  font-family: ${typography.ui};
  font-size: ${typography.size.xs}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.caps};
  text-transform: uppercase;
  color: ${palette.slate};
`;

const Input = styled.input`
  width: 140px;
  height: ${layout.fieldHeight};
  padding: 0 ${spacing.sm};
  border: 1px solid ${palette.line};
  border-radius: ${radius.xs};
  background: ${palette.surface};
  color: ${palette.ink};
  font-family: ${typography.ui};
  font-size: ${typography.size.base}px;

  &:focus {
    outline: none;
    border-color: ${palette.lagoon};
    box-shadow: ${shadow.focus};
  }

  ${down('sm')} {
    width: 100%;
  }
`;

interface AllocateUnitsPanelProps {
  buildingId: number;
  buildingName: string;
  // Passed in rather than re-read, so this panel and its host screen always show
  // the same numbers. The pool has one source of truth; it also gets one reader
  // per screen.
  allowance: UseUnitAllowanceResult;
  onAllocated: () => void;
}

// The step between "create a building" and "move residents in".
//
// An org buys a pool of units; this spends part of it on one building. Allocating
// N at once is a single atomic call rather than N creates, so running out
// half-way cannot leave a building with an arbitrary partial count — the whole
// batch is checked against the allowance before anything is written.
export const AllocateUnitsPanel: React.FC<AllocateUnitsPanelProps> = ({
  buildingId,
  buildingName,
  allowance,
  onAllocated,
}) => {
  const [quantity, setQuantity] = useState('');
  const [busy, setBusy] = useState(false);
  const [upsell, setUpsell] = useState<{ open: boolean; shortfall: number }>({
    open: false,
    shortfall: 1,
  });

  const parsed = Number.parseInt(quantity, 10);
  const valid = Number.isInteger(parsed) && parsed > 0;
  const { remaining, capped, canBuy, allowance: pool } = allowance;

  // Infinity when unmetered, so this is false for an org with no cap — exactly
  // right: there is nothing to run out of.
  const overCap = valid && parsed > remaining;
  const shortfall = useMemo(
    () => (overCap && Number.isFinite(remaining) ? parsed - remaining : 1),
    [overCap, parsed, remaining]
  );

  // `skipPrecheck` is set on the retry that follows a purchase. `remaining` is
  // read from state, and the reload that follows buying has not landed by the
  // time the retry fires — without this the panel would re-open the dialog it
  // was just paid to close. The server is what actually enforces the cap, so
  // skipping the local check is safe: at worst it answers 403 again.
  const submit = async (skipPrecheck = false) => {
    if (!valid || busy) {
      return;
    }

    // Ask before the server has to refuse. The server still enforces it — this
    // is the difference between an explanation and an error.
    if (overCap && !skipPrecheck) {
      setUpsell({ open: true, shortfall });

      return;
    }

    setBusy(true);
    try {
      const result = await allocateUnits(buildingId, parsed);
      toast.success(
        result.skipped > 0
          ? `Added ${result.created} of ${result.requested} units — ${result.skipped} numbers were already taken`
          : `Added ${result.created} unit${
              result.created === 1 ? '' : 's'
            } to ${buildingName}`
      );
      setQuantity('');
      onAllocated();
      await allowance.reload();
    } catch (error) {
      // The server's own count is more current than ours — it is the one that
      // decided. Open the dialog with its numbers, not the ones we were holding.
      if (error instanceof UnitLimitError) {
        const left = error.allowance?.remaining ?? 0;
        setUpsell({ open: true, shortfall: Math.max(1, parsed - left) });
        await allowance.reload();

        return;
      }
      toast.error(
        (error as { response?: { data?: { message?: string } } })?.response
          ?.data?.message || 'Could not add those units. Please try again.'
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Panel>
        <Copy>
          <Heading>Allocate units to {buildingName}</Heading>
          {/* "Account pool:" is load-bearing copy, not decoration. This number
              is ORG-WIDE and it was printed directly beneath a single building's
              name, so creating a second building — which spends from the same
              pool and touches no row of the first — read as the second building
              having taken units off the first. Nothing here is per-building and
              nothing here changes a number; the label is what stops the figure
              being read as this building's allocation. */}
          <Detail>
            {capped ? (
              <>
                <b>Account pool:</b> <b>{remaining}</b> of{' '}
                <b>{pool?.units_allowed ?? 0}</b> units available across your
                account. Units are what you are billed for, so allocating one
                here spends one from that pool.
              </>
            ) : (
              <>
                Add the doors in this building. Your plan has no unit limit, so
                allocate as many as it has.
              </>
            )}
          </Detail>
        </Copy>
        <Field>
          <Label htmlFor="allocate-quantity">How many units</Label>
          <Input
            id="allocate-quantity"
            type="number"
            min={1}
            step={1}
            value={quantity}
            disabled={busy}
            placeholder="24"
            onChange={e => setQuantity(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void submit();
              }
            }}
          />
        </Field>
        {/* Wrapped, not passed directly: the click event would arrive as
            `skipPrecheck` and every click would bypass the pre-check. */}
        <AppButton
          variant="primary"
          disabled={!valid || busy}
          onClick={() => void submit()}
        >
          {busy ? 'Adding…' : 'Allocate'}
        </AppButton>
      </Panel>

      <BuyUnitsDialog
        open={upsell.open}
        reason={
          valid
            ? `Allocating ${parsed} units to ${buildingName}`
            : `Allocating units to ${buildingName}`
        }
        unitsAllowed={pool?.units_allowed ?? null}
        unitsUsed={pool?.units_used ?? 0}
        shortfall={upsell.shortfall}
        canBuy={canBuy}
        comped={allowance.comped}
        returnedCount={allowance.unitsReturned}
        onDismiss={() => setUpsell(u => ({ ...u, open: false }))}
        onBought={async (_allowed, info) => {
          setUpsell(u => ({ ...u, open: false }));
          await allowance.reload();
          // Retry what they were blocked on — being sent back to re-type the
          // same number after paying is the wrong ending to this flow.
          //
          // UNLESS the buyer spent the new capacity RESTORING previously
          // returned units in the dialog's restore step: the pending "create N
          // fresh units" would then mint on top of the restore (or 403 again).
          // The restore already changed the building's inventory; let them
          // look at it and re-decide.
          if (info?.restored) {
            toast.success(
              'Adjust the number below if you still need fresh units.'
            );

            return;
          }
          void submit(true);
        }}
      />
    </>
  );
};
